<template>
    <central-modal
        v-model:visible="isVisible"
        header="Contact Us"
    >
        <div class="contact-form-wrapper" v-if="isVisible">
            <component-loader v-if="isLoading" />
            <form id="mktoForm_1633" v-show="!isLoading" />
        </div>
    </central-modal>
</template>

<script setup lang="ts">
import {
    onUnmounted, ref, watch,
} from 'vue';
import { useToast } from 'primevue/usetoast';
import ComponentLoader from '@/components/layout/ComponentLoader.vue';
import CentralModal from '@/components/common/CentralModal.vue';
import { useCentralModalStore } from '@/stores/centralModalStore.ts';

const props = defineProps<{
    visible: boolean;
    prefillText?: string;
}>();

const emit = defineEmits<{
    'update:visible': [boolean];
}>();

interface MarketoForms {
    loadForm: (url: string, munchkinId: string, formId: number) => void;
    whenReady: (callback: (form: MarketoForm) => void) => void;
}

interface MarketoForm {
    getFormElem(): HTMLFormElement;
    onSuccess: (callback: (values: Record<string, unknown>, followUpUrl?: string) => boolean) => void;
    onValidate: (callback: (valid: boolean) => void) => void;
    submittable: (submittable: boolean) => void;
    submit: () => void;
    setValues: (values: Record<string, unknown>) => void;
}

declare global {
    interface Window {
        MktoForms2: MarketoForms;
    }
}

const MARKETO_BASE_URL = '//insights.lcp.com';
const MARKETO_MUNCHKIN_ID = '032-PAO-331';
const MARKETO_FORM_ID = 1633;
const MARKETO_SCRIPT_URL = '//insights.lcp.com/js/forms2/js/forms2.min.js';

const toast = useToast();
const centralModalStore = useCentralModalStore();

const isVisible = ref<boolean>(props.visible);
const isLoading = ref<boolean>(true);

function loadForm (): void {
    if (window.MktoForms2) {
        window.MktoForms2.loadForm(MARKETO_BASE_URL, MARKETO_MUNCHKIN_ID, MARKETO_FORM_ID);

        window.MktoForms2.whenReady((form) => {
            form.setValues({
                formPageURL: window.location.href,
                ...(props.prefillText ? { question3: props.prefillText } : {}),
            });
            form.onValidate((valid) => {
                if (!valid) {
                    toast.add({
                        severity: 'warn',
                        summary: 'Validation Error',
                        detail: 'Please complete all required fields.',
                        life: 5000,
                    });
                }
            });

            form.onSuccess(() => {
                toast.add({
                    severity: 'success',
                    summary: 'Success',
                    detail: 'Form submitted successfully!',
                    life: 5000,
                });
                centralModalStore.closeCurrentModal();
                return false;
            });

            setTimeout(() => {
                isLoading.value = false;
            }, 1000);
        });
    }
}

function loadMarketoForm (): void {
    if (window.MktoForms2) {
        loadForm();
    } else {
        const script = document.createElement('script');
        script.src = MARKETO_SCRIPT_URL;
        script.async = true;
        script.onload = loadForm;
        script.onerror = (): void => {
            isLoading.value = false;
        };
        document.head.appendChild(script);
    }
}

function unloadMarketoForm (): void {
    const form = document.getElementById('mktoForm_1633');
    if (!form) return;
    form.innerHTML = '';
    isLoading.value = true;
}

onUnmounted(() => {
    unloadMarketoForm();
});

watch(() => props.visible, (newVal) => {
    isVisible.value = newVal;
});

watch(() => isVisible.value, (newVal) => {
    emit('update:visible', newVal);
    if (newVal) {
        loadMarketoForm();
    } else {
        unloadMarketoForm();
    }
});

</script>

<style lang="scss" scoped>

.contact-form-wrapper {
    min-height: 40vh;
    min-width: 40vw;
    height: 100%;
    max-height: 80vh;
    overflow: auto;
}

#mktoForm_1633 {
    width: inherit !important;
    padding: var(--defaultSpacing);
    display: flex;
    flex-direction: column;
    height: 100%;
    justify-content: space-around;

    :deep(.mktoFormRow) {
        input, textarea {
            border-radius: var(--borderRadiusOuter);
        }

        * {
            color: var(--primaryFont);
            font-family: "Montserrat", sans-serif;
            font-size: var(--defaultFontSize);
        }

        .mktoFieldWrap {
            display: grid;
            grid-template-columns: auto 1fr;
            gap: var(--defaultSpacing);
            width: 100%;
            padding: var(--defaultSpacing);
        }

        .mktoFieldDescriptor {
            width: 100%;
            margin-bottom: 0 !important;
        }

        .mktoLabel {
            width: 200px !important;

            em::before {
                content: '\A';
                white-space: pre;
            }
        }

        .mktoLogicalField {
            display: grid;
            grid-template-columns: auto 1fr;
            width: 100% !important;
        }

        .mktoHtmlText {
            width: inherit !important;
        }

        .mktoField {
            width: 100% !important;
        }

        .mktoAsterix {
            color: #bf0000;
        }

        .mktoClear, .mktoOffset, .mktoGutter  {
            display: none;
        }

        .mktoError {
            right: calc(100% - 400px) !important;
            bottom: -30px !important;
        }
    }

    :deep(.mktoCaptchaDisclaimer) {
        padding: var(--defaultSpacing);
        color: var(--primaryFont);
        font-family: "Montserrat", sans-serif !important;

    }

    :deep(.mktoRecaptcha) {
        margin: var(--defaultSpacing) 0;
        width: 100% !important;
    }

    :deep(.mktoButtonRow) {
        padding: var(--defaultSpacing);
        font-family: "Montserrat", sans-serif !important;
        font-size: var(--defaultFontSize);

        .mktoButtonWrap {
            margin-left: 0 !important;
            display: flex;
            gap: var(--defaultSpacing);
        }

        .mktoButton {
            background-image: none;
            background-color: var(--actionColour);
            border-radius: var(--borderRadiusOuter);
            border: none;
            color: white;
        }

        .mktoError {
            position: unset;
            background: none;
            border: none;
            font-family: unset;

            .mktoErrorMsg {
                background: inherit;
                border: inherit;
                box-shadow: none;
                text-shadow: none;
                color: red;
                margin-top: 0;
                white-space: nowrap;
            }

            .mktoErrorArrowWrap {
                display: none;
            }
        }
    }
}

</style>
